import { ComponentType, lazy, LazyExoticComponent } from 'react';
import * as Sentry from '@sentry/react';

const PAGE_HAS_BEEN_REFRESHED_TIME = 'page-has-been-refreshed-time';
const REFRESH_TIMEOUT = 15000;

export type tLazyWithPreload<T extends ComponentType<any>> = LazyExoticComponent<T> & {
  preload: () => Promise<unknown>;
};

function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<any>
): tLazyWithPreload<T> {
  const Lazy = lazy(async function () {
    const pageHasBeenRefreshedTimeString = window.sessionStorage.getItem(
      PAGE_HAS_BEEN_REFRESHED_TIME
    );

    try {
      const component = await componentImport();

      return component;
    } catch (error) {
      const errorTime = Date.now();

      if (
        !pageHasBeenRefreshedTimeString ||
        (pageHasBeenRefreshedTimeString &&
          errorTime - parseInt(pageHasBeenRefreshedTimeString, 10) > REFRESH_TIMEOUT)
      ) {
        window.sessionStorage.setItem(PAGE_HAS_BEEN_REFRESHED_TIME, String(errorTime));

        window.location.reload();

        /* `reload()` returns `undefined` and the navigation it schedules is async, so
         * returning its result would resolve this `lazy()` promise with `undefined`.
         * React then reads `.default` off it and throws
         * "Cannot read properties of undefined (reading 'default')" before the reload
         * lands, turning a silent stale-chunk recovery into an ErrorBoundary screen.
         * Never settling keeps the component suspended until the page goes away. */
        return new Promise<never>(() => {});
      }

      /* The refresh already happened and the chunk is still missing — a genuine
       * deploy/CDN problem rather than a stale tab. Tag it so it groups apart from
       * ordinary runtime errors reaching the ErrorBoundary. */
      Sentry.captureException(error, {
        tags: { chunk_load_failure: 'true' }
      });

      throw error;
    }
  }) as tLazyWithPreload<T>;

  Lazy.preload = () => componentImport().catch(() => undefined);

  return Lazy;
}

export default lazyWithRetry;
